import { memo } from "react";
import { Button } from "@medusajs/ui";
import { useNavigate } from "@tanstack/react-router";
import { ShoppingCart } from "lucide-react";

export interface EmptyCartStateProps {
  /**
   * Variant of empty state to display
   */
  variant: "no-carts" | "no-matching-filters";
  /**
   * Callback when "Clear Filters" button is clicked
   * Only used for "no-matching-filters" variant
   */
  onClearFilters?: () => void;
  /**
   * Optional CSS class name
   */
  className?: string;
}

/**
 * Empty state component for cart page.
 *
 * Variants:
 * - "no-carts": User has no items in cart (truly empty)
 * - "no-matching-filters": User has carts but all are filtered out
 *
 * @example
 * ```tsx
 * // Truly empty cart
 * <EmptyCartState variant="no-carts" />
 *
 * // Filtered out all carts
 * <EmptyCartState
 *   variant="no-matching-filters"
 *   onClearFilters={selectAll}
 * />
 * ```
 */
function EmptyCartStateComponent({
  variant,
  onClearFilters,
  className
}: EmptyCartStateProps) {
  const navigate = useNavigate();

  const handleStartShopping = () => {
    navigate({ to: "/search" });
  };

  if (variant === "no-carts") {
    return (
      <div className={className}>
        <div className="flex min-h-[400px] flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-12">
          <ShoppingCart className="mb-4 h-16 w-16 text-gray-400" />
          <h2 className="mb-2 text-2xl font-semibold text-gray-900">
            Your cart is empty
          </h2>
          <p className="mb-6 text-center text-gray-600">
            Looks like you haven't added anything to your cart yet.
            <br />
            Start shopping to find great products!
          </p>
          <Button onClick={handleStartShopping} size="large">
            Start Shopping
          </Button>
        </div>
      </div>
    );
  }

  // variant === "no-matching-filters"
  return (
    <div className={className}>
      <div className="flex min-h-[400px] flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-12">
        <ShoppingCart className="mb-4 h-16 w-16 text-gray-400" />
        <h2 className="mb-2 text-2xl font-semibold text-gray-900">
          No items match your filter
        </h2>
        <p className="mb-6 text-center text-gray-600">
          You have items in your cart, but none match the selected filters.
          <br />
          Try clearing your filters to see all items.
        </p>
        {onClearFilters && (
          <Button onClick={onClearFilters} size="large" variant="secondary">
            Clear Filters
          </Button>
        )}
      </div>
    </div>
  );
}

export const EmptyCartState = memo(EmptyCartStateComponent);
